// Textos rapidos y firmas (objeto plantillaCorreo): el selector que se usa al escribir un correo y la firma que va
// segun el buzon desde el que se envia (la de la empresa para ese buzon, si no la personal de Mi perfil).
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { type Plantilla, plantillas } from '../api/correoEstado';
import { useSession } from '../auth/session';
import { datosAgente, type DatosPlantilla, firmaDe, rellenar } from '../lib/correo';
import { normalize } from '../lib/format';
import { Icon } from '../lib/icons';
import { leerFirma } from './Buzones';

export const usePlantillas = () => useQuery({ queryKey: ['plantillas'], queryFn: plantillas, staleTime: 5 * 60_000 });

// Firma lista para enviar desde un buzon, con las variables del agente ya puestas.
export function useFirma(buzon: string | undefined) {
  const { yo } = useSession();
  const p = usePlantillas();
  return useMemo(() => {
    const firmas = (p.data ?? []).filter((x) => x.tipo === 'FIRMA');
    return rellenar(firmaDe(buzon ?? '', firmas, leerFirma(yo.email)), datosAgente(yo.nombre, yo.email));
  }, [p.data, buzon, yo.email, yo.nombre]);
}

// Boton "Texto rapido": lista con busqueda; al elegir, devuelve el texto con las variables llenas.
export function TextoRapido({ datos, onElegir }: { datos: DatosPlantilla; onElegir: (texto: string) => void }) {
  const { yo } = useSession();
  const p = usePlantillas();
  const [ancla, setAncla] = useState<DOMRect | null>(null);
  const [q, setQ] = useState('');
  const lista = useMemo(() => {
    const nq = normalize(q.trim());
    return (p.data ?? []).filter((x: Plantilla) => x.tipo !== 'FIRMA' && (!nq || normalize(`${x.nombre} ${x.cuerpo}`).includes(nq)));
  }, [p.data, q]);
  const elegir = (x: Plantilla) => {
    onElegir(rellenar(x.cuerpo, { ...datosAgente(yo.nombre, yo.email), ...datos }));
    setAncla(null);
    setQ('');
  };
  return (
    <>
      <button type="button" className="btn sm ghost" onClick={(e) => setAncla(e.currentTarget.getBoundingClientRect())} aria-haspopup="menu">
        <Icon name="note" /> <span className="lbl-m">Texto rápido</span>
      </button>
      {ancla
        ? createPortal(
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 89 }} onClick={() => setAncla(null)} />
              <div className="menu tr-menu" role="menu" style={{ bottom: Math.max(8, window.innerHeight - ancla.top + 6), left: Math.min(ancla.left, window.innerWidth - 348) }}>
                <input autoFocus className="inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar texto rápido…" aria-label="Buscar texto rápido" />
                <div className="tr-lista">
                  {lista.length ? (
                    lista.map((x) => (
                      <button key={x.id} role="menuitem" onClick={() => elegir(x)}>
                        <b>{x.nombre}</b>
                        <span>{x.cuerpo.slice(0, 90)}</span>
                      </button>
                    ))
                  ) : (
                    <p className="note" style={{ padding: 10 }}>
                      {p.isLoading ? 'Cargando…' : 'No hay textos rápidos. Se crean en Administración → Plantillas.'}
                    </p>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
}
